import type { Conditions } from "../conditions";

type BlockConditions = { id: number | string; conditions: Partial<Conditions.Condition>[] };

// JSON-serialised so a "|" inside a user-entered option value can't collide two
// distinct conditions into the same signature.
export const conditionSignature = (c: Partial<Conditions.Condition>): string =>
  JSON.stringify([c.actionType ?? null, c.objectId ?? null, c.operatorType ?? null, c.value ?? null]);

export interface SharedConditionsResult {
  conditions: Partial<Conditions.Condition>[];
  idMaps: Record<string, number | string>[];
  hasExtra: boolean;
}

export const computeSharedConditions = (blocks: BlockConditions[]): SharedConditionsResult => {
  if (blocks.length === 0) return { conditions: [], idMaps: [], hasExtra: false };

  const totalCount = blocks.reduce((sum, b) => sum + (b.conditions?.length ?? 0), 0);

  const first = blocks[0].conditions ?? [];
  const conditions: Partial<Conditions.Condition>[] = [];
  const idMaps: Record<string, number | string>[] = [];

  first.forEach((candidate) => {
    const sig = conditionSignature(candidate);
    const idMap: Record<string, number | string> = {};

    const presentOnAll = blocks.every((b) => {
      // If a block has multiple conditions with the same signature, only the first
      // is tracked in idMaps; hasExtra will be true to signal the unrepresented ones.
      const match = (b.conditions ?? []).find((c) => conditionSignature(c) === sig);
      if (!match || match.id === undefined) return false;
      idMap[String(b.id)] = match.id;
      return true;
    });

    if (presentOnAll && !conditions.some((c) => conditionSignature(c) === sig)) {
      conditions.push({ ...candidate });
      idMaps.push(idMap);
    }
  });

  const hasExtra = totalCount > conditions.length * blocks.length;
  return { conditions, idMaps, hasExtra };
};
